import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { WebMCPProvider } from "@cogo/webmcp-runtime/react";
import { cogoChatAdapter } from "@/features/chat/services/chat-model-adapter";
import { useStudioStore } from "@/store/studio-store";
import { resolveTemplateDeeplinkMode } from "@/store/ide-v2-slice";
import { getCurrentThreadId } from "@/store/selectors";
import { StudioShell } from "@/shell/studio-shell";
import { StudioDashboardShell } from "@/features/studio-dashboard/studio-dashboard-shell";
import { TemplateDetailPage } from "@/features/project/components/template-detail-page";
import { TailarkLandingPage } from "@/features/project/components/tailark-landing-page";
import { AuthPage } from "@/features/auth/auth-page";
import { replaceAuthenticatedLoginRoute } from "@/features/auth/authenticated-login-route";
import {
  replaceApplianceEntryRoute,
  shouldRewriteToStudioEntry,
} from "@/features/auth/appliance-entry-route";
import {
  installStudioBridge,
  uninstallStudioBridge,
} from "@/lib/studio-bridge";
import {
  installDesignSystemPackReleaseDevTrigger,
  uninstallDesignSystemPackReleaseDevTrigger,
} from "@/features/design-system/lib/design-system-pack-release-dev-trigger";
import { BrainstormOverlayHost } from "@/shell/brainstorm-overlay-host";
import { useKg } from "@/features/design-system/kg-provider";
import {
  createStudioRegistry,
  STUDIO_ONTOLOGY,
  STUDIO_AGENT_CONFIG,
  subscribeStudioMcpToolsToRegistry,
  syncStudioMcpToolsIntoRegistry,
} from "@/agent";
import { handleAboxDeeplink } from "@/features/design-system/ontology/abox-app-deeplink";
import {
  canonicalStudioPathFromPathname,
  isKnownStudioPathname,
  resolveStudioRouteFromPathname,
} from "@/lib/studio-route-map";
import {
  buildLandingStudioNotice,
  readLandingStudioHandoff,
} from "@/lib/landing-studio-handoff";
import {
  activateOpenPencilSourceWorkspace,
  isOpenPencilSourceWorkspacePath,
  OPENPENCIL_SOURCE_WORKSPACE_ROUTE_ID,
} from "@/features/openpencil-figma/openpencil-source-navigation";

// New faithful prototype-port marketing surfaces (cogo-marketing/).
// Replaces older HomePage / BlogPage / DocsPage / DesignSystemPage /
// AboutPage / TemplatesPage at the marketing routes.
const MktLanding = React.lazy(() => import('@/pages/cogo-marketing/Landing'))
const MktShowroom = React.lazy(() => import('@/pages/cogo-marketing/Showroom'))
const MktShowroomProduct = React.lazy(() => import('@/pages/cogo-marketing/ShowroomProductPage'))
const MktHowItWorks = React.lazy(() => import('@/pages/cogo-marketing/HowItWorks'))
const MktProof = React.lazy(() => import('@/pages/cogo-marketing/Proof'))
const MktOwnership = React.lazy(() => import('@/pages/cogo-marketing/Ownership'))
const MktBlog = React.lazy(() => import('@/pages/cogo-marketing/Blog'))
const MktAbout = React.lazy(() => import('@/pages/cogo-marketing/About'))
const MktDocsLanding = React.lazy(() => import('@/pages/cogo-marketing/DocsLanding'))
const MktDesignSystem = React.lazy(() => import('@/pages/cogo-marketing/DesignSystem'))
const MktDevelopers = React.lazy(() => import('@/pages/cogo-marketing/Developers'))

// Legacy public pages — kept reachable for direct links (terms / privacy / etc).
const ContactPage = React.lazy(
  () => import("@/pages/homepage/pages/ContactPage"),
);
const PricingPage = React.lazy(
  () => import("@/pages/homepage/pages/PricingPage"),
);
const EnterprisePage = React.lazy(
  () => import("@/pages/homepage/pages/EnterprisePage"),
);
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));
const StudioNotFoundPage = React.lazy(
  () => import("@/pages/StudioNotFoundPage"),
);
const TermsPage = React.lazy(() => import("@/pages/homepage/pages/TermsPage"));
const PrivacyPage = React.lazy(
  () => import("@/pages/homepage/pages/PrivacyPage"),
);
const RefundPage = React.lazy(
  () => import("@/pages/homepage/pages/RefundPage"),
);
const CheckoutSuccessPage = React.lazy(
  () => import("@/pages/checkout/CheckoutSuccessPage"),
);
const CheckoutFailurePage = React.lazy(
  () => import("@/pages/checkout/CheckoutFailurePage"),
);
const CheckoutPendingPage = React.lazy(
  () => import("@/pages/checkout/CheckoutPendingPage"),
);
const AppViewPage = React.lazy(() => import("@/pages/AppViewPage"));
const PreviewSnapshotPage = React.lazy(
  () => import("@/pages/PreviewSnapshotPage"),
);
const TemplateDetailPublicPage = React.lazy(
  () => import("@/pages/TemplateDetailPublicPage"),
);
const DocsRouter = React.lazy(() => import("@/pages/docs-site/DocsRouter"));
// Phase 396-04 (QI-04) — manual site at /docs/manual/en/* (D-04)
// TODO(v68+): Re-implement as Coogo JSON runtime (D-06)
const ManualLayoutPage = React.lazy(
  () => import("@/pages/docs/manual/ManualLayoutPage"),
);

let cachedIdeShellHarnessRoute: React.LazyExoticComponent<React.ComponentType> | null =
  null;

function getIdeShellHarnessRoute() {
  cachedIdeShellHarnessRoute ??= React.lazy(() =>
    import("@/__smoke__/IdeShellHarnessRoute").then((m) => ({
      default: m.IdeShellHarnessRoute,
    })),
  );
  return cachedIdeShellHarnessRoute;
}

let cachedWordGardCollaborationRoute: React.LazyExoticComponent<React.ComponentType> | null =
  null;

function getWordGardCollaborationRoute() {
  cachedWordGardCollaborationRoute ??= React.lazy(() =>
    import("@/features/wordgard-collaboration/components/wordgard-collaboration-surface").then(
      (m) => ({ default: m.WordGardCollaborationSurface }),
    ),
  );
  return cachedWordGardCollaborationRoute;
}

let cachedDocumentWorkspaceHarnessRoute: React.LazyExoticComponent<React.ComponentType> | null =
  null;

function getDocumentWorkspaceHarnessRoute() {
  cachedDocumentWorkspaceHarnessRoute ??= React.lazy(() =>
    import("@/__smoke__/DocumentWorkspaceHarnessRoute").then((m) => ({
      default: m.DocumentWorkspaceHarnessRoute,
    })),
  );
  return cachedDocumentWorkspaceHarnessRoute;
}

type IdeServerOpenState = Pick<
  ReturnType<typeof useStudioStore.getState>,
  "view" | "currentProject" | "currentProjectId" | "projectSource"
>;

export function shouldOpenServerProjectFromIde(
  projectId: string,
  store: IdeServerOpenState,
): boolean {
  if (
    projectId.startsWith("import-") ||
    projectId === OPENPENCIL_SOURCE_WORKSPACE_ROUTE_ID
  ) {
    return false;
  }
  if (
    store.currentProjectId === projectId &&
    store.currentProject &&
    store.projectSource !== "server"
  ) {
    return false;
  }
  return store.view === "dashboard";
}

function readIdeProjectId(path: string): string | null {
  if (!path.startsWith("/ide/")) return null;
  return path.split("/").filter(Boolean)[1] ?? null;
}

/**
 * Rewrite the address bar when a Studio URL is a non-canonical spelling of a
 * route it does resolve to — an alias (`/studio/audience` → `/studio/members`)
 * or a stray trailing slash (`/studio/` → `/studio`). Screen and address must
 * agree; a URL that stays wrong after landing on the right screen is the same
 * silent failure as landing on the wrong screen.
 *
 * `search` and `hash` are carried over: the previous inline version replaced
 * the whole URL, so an aliased deep link lost its query string.
 */
function applyCanonicalStudioPath(
  pathname: string,
  setRoute: (path: string) => void,
): void {
  const canonicalPath = canonicalStudioPathFromPathname(pathname);
  if (!canonicalPath) return;
  window.history.replaceState(
    null,
    "",
    `${canonicalPath}${window.location.search}${window.location.hash}`,
  );
  setRoute(canonicalPath);
}

function shouldRestoreImportedProjectFromIde(
  projectId: string,
  store: IdeServerOpenState,
): boolean {
  return (
    projectId.startsWith("import-") &&
    (store.currentProjectId !== projectId || !store.currentProject)
  );
}

// Phase 5 (IA v3): Commerce is now a sub-area of the Dashboard shell, not
// its own shell. `commerce` and its sub-routes (`products` / `orders` /
// `revenue` / `members` / `billing` / `analytics`) all render through
// `StudioDashboardShell`'s island registry. Historical note: Phase 2
// (PR #229) introduced a dedicated `StudioCommerceShell` with its own rail
// + top bar; Phase 5 reverted it because the extra chrome blocked
// clickable breadcrumbs, header search, and the chat affordance. Commerce is
// agentic-driven — the operator talks to the agent via chat — so a thin
// dashboard sub-area matches the workflow better than a second shell.

export function App() {
  const view = useStudioStore((s) => s.view);
  const isAuthLoading = useStudioStore((s) => s.isAuthLoading);
  const session = useStudioStore((s) => s.session);
  const explicitSignOut = useStudioStore((s) => s.explicitSignOut);
  const initAuth = useStudioStore((s) => s.initAuth);
  const _hydrateStudioMode = useStudioStore((s) => s._hydrateStudioMode);
  const currentProject = useStudioStore((s) => s.currentProject);
  const projectLoadError = useStudioStore((s) => s.projectLoadError);
  const runtime = useLocalRuntime(cogoChatAdapter);

  // Expose chat runtime for E2E testing and direct WebMCP orchestration
  useEffect(() => {
    if (import.meta.env.VITE_E2E_MOCK === "true" || import.meta.env.DEV) {
      (window as any).__cogo_chat_runtime__ = runtime;
    }
    return () => {
      delete (window as any).__cogo_chat_runtime__;
    };
  }, [runtime]);

  // Phase 4 IA-3 — derived thread id keys the AssistantRuntimeProvider so
  // the in-memory transcript resets when the user crosses a thread boundary
  // (Dashboard/Commerce ↔ IDE project). Re-subscribes are cheap because the
  // adapter is pure.
  const currentThreadId = useStudioStore(getCurrentThreadId);

  // Phase 4 IA-5 — one-shot WebMCP registry for the Studio session. The
  // registry owns all agentic tools; handlers read the live store so no
  // re-creation is needed when project/shell changes.
  const webmcpRegistry = useMemo(() => createStudioRegistry(), []);

  const [route, setRoute] = useState(window.location.pathname);
  const landingHandoffNoticeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    if (route !== "/login" || isAuthLoading || !session) return;
    replaceAuthenticatedLoginRoute(window);
    setRoute(window.location.pathname);
    window.scrollTo(0, 0);
  }, [isAuthLoading, route, session]);

  // Appliance entry (operator, 2026-08-10; predicate widened 2026-08-18): the
  // first screen is the sign-in screen. `/` and `/homepage` canonicalize to
  // `/studio`, whose auth gate renders the id/password AuthPage when
  // unauthenticated. Cloud/dev builds keep the marketing landing.
  //
  // `shouldRewriteToStudioEntry` — not `isCogoAuthProvider && isApplianceEntryRoute`
  // — because "login is the first screen" is a property of the DISTRIBUTION,
  // not of which identity service happens to be configured; and because the
  // same call also removes the public commerce pages (`/pricing`,
  // `/checkout/*`, `/refund`) that App serves from `INFO_PAGES` below,
  // before the auth gate, at the appliance's own origin.
  useEffect(() => {
    if (!shouldRewriteToStudioEntry(route)) return;
    replaceApplianceEntryRoute(window);
    setRoute(window.location.pathname);
    window.scrollTo(0, 0);
  }, [route]);

  // Install Studio Bridge API (window.__cogo_studio__)
  useEffect(() => {
    installStudioBridge();
    // F5 — DEV-only pack-release generation trigger (no-op in prod builds).
    installDesignSystemPackReleaseDevTrigger();
    syncStudioMcpToolsIntoRegistry(webmcpRegistry);
    const unsubscribeStudioMcpTools =
      subscribeStudioMcpToolsToRegistry(webmcpRegistry);
    return () => {
      unsubscribeStudioMcpTools();
      uninstallDesignSystemPackReleaseDevTrigger();
      uninstallStudioBridge();
    };
  }, [webmcpRegistry]);

  // Sync studioRoute from URL on initial page load
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (
      path.startsWith("/studio") &&
      !params.has("projectId") &&
      !params.has("template")
    ) {
      const store = useStudioStore.getState();
      // Unresolvable Studio URLs leave `studioRoute` alone and fall through
      // to the not-found branch in render — writing a fallback route here is
      // what used to make a bad address render as Home.
      const resolved = resolveStudioRouteFromPathname(path);
      if (resolved) store.setStudioRoute(resolved);
      applyCanonicalStudioPath(path, setRoute);
    }
  }, []);

  useEffect(() => {
    if (isOpenPencilSourceWorkspacePath(window.location.pathname)) {
      activateOpenPencilSourceWorkspace({ navigate: false });
    }
  }, []);

  useEffect(() => {
    if (!route.startsWith("/studio")) return;
    const handoff = readLandingStudioHandoff();
    if (!handoff) return;
    const noticeKey = `${handoff.createdAt}:${handoff.prompt}`;
    if (landingHandoffNoticeKeyRef.current === noticeKey) return;
    landingHandoffNoticeKeyRef.current = noticeKey;
    const store = useStudioStore.getState();
    store.setPendingChatNotice(buildLandingStudioNotice(handoff));
    store.setChatSidebarOpen(true);
    store.setInspectorPanelOpen(false);
  }, [route]);

  // Sync browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      setRoute(path);
      const store = useStudioStore.getState();
      if (path === "/" || path === "/homepage") {
        // Navigating back to homepage — reset view so App renders HomePage
        // (prevents template-detail → back landing on studio dashboard)
        if (store.view === "template-detail") {
          store.closeTemplateDetail();
        }
      } else if (path.startsWith("/studio")) {
        // Reset to dashboard view when going back to dashboard route
        if (store.view === "workspace") {
          store.closeProject();
        }
        const resolved = resolveStudioRouteFromPathname(path);
        if (resolved) store.setStudioRoute(resolved);
        applyCanonicalStudioPath(path, setRoute);
      } else if (path.startsWith("/ide/")) {
        if (isOpenPencilSourceWorkspacePath(path)) {
          activateOpenPencilSourceWorkspace({ navigate: false });
          return;
        }
        const projectId = readIdeProjectId(path);
        if (
          projectId &&
          shouldRestoreImportedProjectFromIde(projectId, store)
        ) {
          void store.restoreImportedCogoApplication(projectId);
        } else if (
          projectId &&
          shouldOpenServerProjectFromIde(projectId, store)
        ) {
          store.setProjectLoadError(null);
          store.openServerProject(projectId);
        }
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // URL deeplink: /studio?projectId=xxx → open server project
  // URL deeplink: /studio?template=xxx → open local template directly (no DB)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get("projectId");
    const templateId = params.get("template");
    if (projectId && view === "dashboard") {
      // Reset stale error from previous attempt before loading
      useStudioStore.getState().setProjectLoadError(null);
      useStudioStore.getState().openServerProject(projectId);
    } else if (templateId && view === "dashboard") {
      useStudioStore.getState().openProject(templateId);
      // Phase 039-02 (D-039-4b) — IDE v2 deeplink bridge. `openProject` sets
      // `view='workspace'`, so StudioShell mounts the v2 `IDEShellLayout`
      // (`ideV2Enabled` defaults true). That layout's `isEmpty` gate is
      // `studioMode === 'build' && dispatchedOutput === null`; `studioMode`
      // defaults `'build'`, so a `?template=` deeplink would show the chat
      // `EmptyState` page while the loaded template sits hidden in
      // `active-canvas-layer` at `opacity-0`. Flip to `'edit'` so the loaded
      // template renders — UNLESS the deeplink explicitly asks for the
      // chat-first build entry via `&mode=build` (real-mode dogfooding /
      // chat-driven starts). Scoped to the `?template=` deeplink path ONLY —
      // a normal project-open keeps its chat-entry (`build`) UX.
      useStudioStore
        .getState()
        .setStudioMode(resolveTemplateDeeplinkMode(params));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL deeplink: /ide/xxx → open server project directly from bare URL
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/ide/")) {
      if (isOpenPencilSourceWorkspacePath(path)) {
        activateOpenPencilSourceWorkspace({ navigate: false });
        return;
      }

      const projectId = readIdeProjectId(path);
      const store = useStudioStore.getState();
      if (projectId && shouldRestoreImportedProjectFromIde(projectId, store)) {
        void store.restoreImportedCogoApplication(projectId);
      } else if (
        projectId &&
        shouldOpenServerProjectFromIde(projectId, store)
      ) {
        store.setProjectLoadError(null);
        store.openServerProject(projectId);
      }
    }
  }, [view]);

  // URL deeplink: /studio?app_id=xxx&tenant_id=yyy → load the assembled app
  // from cogo.application_ontology (Phase 033 Plan 02 / CLOSE-01 / D-CLOSE01-A).
  // ADDITIVE alongside the ?projectId=/?template= effect above (NOT a modify):
  // `handleAboxDeeplink` is gated by isLiveAboxEnabled() (HARD VITE_E2E_MOCK
  // guard FIRST) && deriveTenantId(session) !== null, runs under the
  // authenticated @/lib/supabase client (R-033-02), is boot-race-safe
  // (subscribe-and-retry on the null→populated session), and is idempotent —
  // so the 254-suite + non-ABox paths are unaffected.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aboxAppId = params.get("app_id");
    if (aboxAppId && view === "dashboard") {
      handleAboxDeeplink(window.location.search);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const store = useStudioStore.getState();
    store._hydrateInspectorPosition();
    store._hydrateDevtoolsDock();
    store._hydrateStudioMode();
    store._hydrateStudioPersona();
    store._hydrateOnboarding();
    // Real-mode opt-in (Phase A P2): defined since the opt-in shipped but never
    // invoked, so the persisted studio.useRealChannel2 flag silently never
    // restored across sessions (found dogfooding real-mode, 2026-06-11).
    store._hydrateRealChannel2FromStorage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Gap 2 — Background sync timer: periodically flushes pending brainstorm
  // observations from the local queue to the Worker via best-effort mode.
  // Fires when pending >= MIN_BATCH_SIZE OR oldest record is >= MAX_STALE_MS old.
  // Silent no-op when nothing pending. Spec §3.6 / plan Task 15.
  const { repo: kg, ready: kgReady } = useKg();
  useEffect(() => {
    if (!kgReady || !kg) return;
    const POLL_INTERVAL_MS = 15_000;
    const MIN_BATCH_SIZE = 5;
    const MAX_STALE_MS = 30_000;
    const id = window.setInterval(async () => {
      try {
        const found = await kg.findObservations({ include: "local" });
        const pending = found.data.filter((r) => r.syncStatus === "pending");
        if (pending.length === 0) return;
        const oldestAgeMs = Date.now() - Date.parse(pending[0]!.observedAt);
        if (pending.length < MIN_BATCH_SIZE && oldestAgeMs < MAX_STALE_MS)
          return;
        await kg.syncObservations();
      } catch (err) {
        // Best-effort — observations survive in localStorage until next try.
        console.debug("[brainstorm] background sync failed", err);
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [kg, kgReady]);

  // Dev-only E2E bridge — exposes the live KG repo on `window` so Playwright
  // specs can poll `findObservations({ include: 'cross-session' })` without
  // reaching through React internals. Gated on `import.meta.env.DEV` so the
  // assignment + branch are DCE-stripped in real-prod builds. Consumer:
  // `e2e/agentic-full-loop.spec.ts` (v2 Phase C Task 9).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!kgReady || !kg) return;
    // @ts-expect-error — intentional dev-only window augmentation
    window.__cogoKgForE2E = kg;
    return () => {
      // @ts-expect-error — intentional dev-only window augmentation
      delete window.__cogoKgForE2E;
    };
  }, [kg, kgReady]);

  const handleNavigate = useCallback((target: string) => {
    setRoute(target);
    window.history.pushState(null, "", target);
    window.scrollTo(0, 0);
    if (target === "/studio" || target.startsWith("/studio")) {
      // Check for template deeplink: /studio?template=xxx
      const url = new URL(target, window.location.origin);
      const templateId = url.searchParams.get("template");
      const projectId = url.searchParams.get("projectId");
      if (templateId) {
        useStudioStore.getState().openProject(templateId);
        // Phase 039-02 (D-039-4b) — same v2 deeplink bridge as the initial-load
        // `?template=` useEffect above: flip studioMode out of the default
        // 'build' so IDEShellLayout renders the loaded template instead of the
        // chat EmptyState (unless `&mode=build` opts into the chat-first
        // entry). Scoped to the `?template=` navigation path only.
        useStudioStore
          .getState()
          .setStudioMode(resolveTemplateDeeplinkMode(url.searchParams));
        return;
      }
      if (projectId) {
        // Reset stale error from previous attempt before loading
        useStudioStore.getState().setProjectLoadError(null);
        useStudioStore.getState().openServerProject(projectId);
        return;
      }
      useStudioStore.getState().closeProject();
      const resolved = resolveStudioRouteFromPathname(url.pathname);
      if (resolved) useStudioStore.getState().setStudioRoute(resolved);
      applyCanonicalStudioPath(url.pathname, setRoute);
    }
  }, []);

  // Dev-only WASM smoke test (Pre-Phase §1.2.5 + §1.2.6). Gate: DEV build
  // WS-E S4 — operator knowledge-candidate queue (design §5 step 3). NOT
  // dev-gated: operators use it in prod; access is gated server-side by the
  // /api/knowledge/queue proxy (Supabase user auth + operator allowlist).
  if (route === "/operator/knowledge-queue") {
    const KnowledgeQueueRoute = React.lazy(() =>
      import("@/features/knowledge-queue/KnowledgeQueueRoute").then((m) => ({
        default: m.KnowledgeQueueRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading knowledge queue…
          </div>
        }
      >
        <KnowledgeQueueRoute />
      </React.Suspense>
    );
  }

  // OR prod build with VITE_ENABLE_WASM_SMOKE=1 (used for prod-preview
  // smoke deploys). Real-prod deploys don't set the flag, so the flag is
  // statically undefined at build time and the entire branch — including
  // the lazy import — is eliminated by dead-code elimination.
  if (
    (import.meta.env.DEV || import.meta.env.VITE_ENABLE_WASM_SMOKE === "1") &&
    route === "/__wasm-test"
  ) {
    const WasmTestRoute = React.lazy(() =>
      import("@/__smoke__/WasmTestRoute").then((m) => ({
        default: m.WasmTestRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading WASM smoke…
          </div>
        }
      >
        <WasmTestRoute />
      </React.Suspense>
    );
  }

  // Dev-only DS v4 primitives showcase — Sub-project A PR 3a/3b smoke route.
  // Visual verification of the 10 new primitives + button/input across all
  // variants. DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_PRIMITIVES_SMOKE === "1") &&
    route === "/__primitives"
  ) {
    const PrimitivesSmokeRoute = React.lazy(() =>
      import("@/__smoke__/PrimitivesSmokeRoute").then((m) => ({
        default: m.PrimitivesSmokeRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading primitives smoke…
          </div>
        }
      >
        <PrimitivesSmokeRoute />
      </React.Suspense>
    );
  }

  // Dev-only Task 4 (ontology-ux-contract cycle) readiness-panel smoke route
  // — identical pattern to the primitives smoke above. Visual verification
  // of <ReadinessPanel> (clean/blocking/error fixtures) ahead of any
  // production call site. DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_GENERATION_READINESS_SMOKE === "1") &&
    route === "/__generation-readiness"
  ) {
    const GenerationReadinessSmokeRoute = React.lazy(() =>
      import("@/__smoke__/GenerationReadinessSmokeRoute").then((m) => ({
        default: m.GenerationReadinessSmokeRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading generation readiness smoke…
          </div>
        }
      >
        <GenerationReadinessSmokeRoute />
      </React.Suspense>
    );
  }

  // Dev-only DS Track A binder runtime smoke — identical pattern to WASM smoke above.
  // DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV || import.meta.env.VITE_ENABLE_BINDER_SMOKE === "1") &&
    route === "/__binder-smoke"
  ) {
    const BinderSmokeRoute = React.lazy(() =>
      import("@/__smoke__/BinderSmokeRoute").then((m) => ({
        default: m.BinderSmokeRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading binder smoke…
          </div>
        }
      >
        <BinderSmokeRoute />
      </React.Suspense>
    );
  }

  // Dev-only KG smoke — Phase 2b Judge first consumer (Step 1b.6 pre-ramp).
  // Exercises useKg().repo across Repository methods + MCP gateway direct
  // probes to measure local-vs-server latency for A/B rollout decisions.
  // DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV || import.meta.env.VITE_ENABLE_KG_SMOKE === "1") &&
    route === "/__kg-smoke"
  ) {
    const KgSmokeRoute = React.lazy(() =>
      import("@/__smoke__/KgSmokeRoute").then((m) => ({
        default: m.KgSmokeRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading KG smoke…
          </div>
        }
      >
        <KgSmokeRoute />
      </React.Suspense>
    );
  }

  // Dev-only ActionFlow host-wiring smoke — proves the renderer-v2 host layer
  // (emitEvent / openModal / invokeCallback / mediaControl / cropImage) works
  // end-to-end in a real browser. DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_HOST_WIRING_SMOKE === "1") &&
    route === "/__host-wiring"
  ) {
    const HostWiringSmokeRoute = React.lazy(() =>
      import("@/__smoke__/HostWiringSmokeRoute").then((m) => ({
        default: m.HostWiringSmokeRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading host-wiring smoke…
          </div>
        }
      >
        <HostWiringSmokeRoute />
      </React.Suspense>
    );
  }

  // Dev-only STUDIO host-effects harness — proves the bespoke modals
  // (ContactAgentModal / ScheduleTourModal) + the auto-registering video
  // renderer (mediaControl) work end-to-end through the real studio host
  // services + a real <AppV2>. Driven by e2e/host-effects.spec.ts. DCE-stripped
  // from real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_HOST_WIRING_SMOKE === "1") &&
    route === "/__host-effects"
  ) {
    const HostEffectsHarnessRoute = React.lazy(() =>
      import("@/__smoke__/HostEffectsHarnessRoute").then((m) => ({
        default: m.HostEffectsHarnessRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading host-effects harness…
          </div>
        }
      >
        <HostEffectsHarnessRoute />
      </React.Suspense>
    );
  }

  // Dev-only STUDIO responsive ide-v2 shell harness (Phase 531) — mounts the real
  // IDEShellLayout with a placeholder canvas child (no auth, no generative chat
  // flow, no build-mode-canvas) so e2e/shell/responsive.spec.ts can run the
  // viewport matrix against the live shell. DCE-stripped from real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_HOST_WIRING_SMOKE === "1") &&
    route === "/__ide-shell"
  ) {
    // Keep the lazy component identity stable across Studio store updates.
    // Report workspace E2E applies semantic edits before capturing proof; if
    // this lazy type is recreated on every App render, Suspense can repaint the
    // harness fallback even though the IDE DOM assertions have already passed.
    const IdeShellHarnessRoute = getIdeShellHarnessRoute();
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading ide-shell harness…
          </div>
        }
      >
        <AssistantRuntimeProvider
          key={`ide-shell-harness:${currentThreadId}`}
          runtime={runtime}
        >
          <IdeShellHarnessRoute />
        </AssistantRuntimeProvider>
      </React.Suspense>
    );
  }

  // Dev-only collaboration adapter surface.
  // Mounts a seeded helper document + preview anchor + Cogo agent proposal so
  // the collaboration contract can be validated without exposing editor
  // internals in the main Studio rail. DCE-stripped from real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_WORDGARD_COLLABORATION_SURFACE === "1") &&
    route === "/__cogo-collaboration-document"
  ) {
    const WordGardCollaborationRoute = getWordGardCollaborationRoute();
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading collaboration document...
          </div>
        }
      >
        <WordGardCollaborationRoute />
      </React.Suspense>
    );
  }

  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_DOCUMENT_WORKSPACE_SMOKE === "1") &&
    route === "/__document-workspace"
  ) {
    const DocumentWorkspaceHarnessRoute = getDocumentWorkspaceHarnessRoute();
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading document workspace...
          </div>
        }
      >
        <DocumentWorkspaceHarnessRoute />
      </React.Suspense>
    );
  }

  // Dev-only ActionFlow state-machine validation smoke page. Mounts the real
  // ValidationTab with a seeded app + runtime trace so Playwright can verify
  // the Studio State Flow surface without invoking generation.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_STATE_FLOW_SMOKE === "1") &&
    route === "/__state-flow-validation"
  ) {
    const StateFlowValidationRoute = React.lazy(() =>
      import("@/__smoke__/StateFlowValidationRoute").then((m) => ({
        default: m.StateFlowValidationRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading state-flow validation…
          </div>
        }
      >
        <StateFlowValidationRoute />
      </React.Suspense>
    );
  }

  // Dev-only Flywheel Cycle 0 observations inspector.
  // Renders the brainstorm picks localStorage sink landed by PR #144 so
  // Cycle 1 (organic use) is observable and Cycle 2 measurement is trivial.
  // DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_FLYWHEEL_INSPECTOR === "1") &&
    route === "/__flywheel"
  ) {
    const FlywheelObservationsRoute = React.lazy(() =>
      import("@/__smoke__/FlywheelObservationsRoute").then((m) => ({
        default: m.FlywheelObservationsRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading flywheel inspector…
          </div>
        }
      >
        <FlywheelObservationsRoute />
      </React.Suspense>
    );
  }

  // Dev-only Phase 3 shell-as-cogo-app block preview.
  // Renders raw `blocks-v2/studio/*` sources via BlockV2 with a local-state
  // override textarea. Parallel to BinderSmoke but scoped to raw sources
  // instead of migrator-bound corpus apps.
  // DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_STUDIO_BLOCKS_PREVIEW === "1") &&
    route === "/__studio-blocks-preview"
  ) {
    const StudioBlocksPreviewRoute = React.lazy(() =>
      import("@/__smoke__/StudioBlocksPreviewRoute").then((m) => ({
        default: m.StudioBlocksPreviewRoute,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading studio blocks preview…
          </div>
        }
      >
        <StudioBlocksPreviewRoute />
      </React.Suspense>
    );
  }

  // Phase A Sub-plan A3.1 — Channel 1 dispatcher dev playground.
  // /__channel-1 — Storybook-style shard browser for findExamples → bindExample
  // → AppV2 render. DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV || import.meta.env.VITE_ENABLE_CHANNEL_1 === "1") &&
    route === "/__channel-1"
  ) {
    const ChannelOnePlayground = React.lazy(
      () => import("@/routes/labs/channel-1/page"),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading Channel 1 playground…
          </div>
        }
      >
        <ChannelOnePlayground />
      </React.Suspense>
    );
  }

  // Phase A Sub-plan A3.2 — Channel 2 mock dispatcher dev playground.
  // /__channel-2 — Storybook-style mock SSE consumer with multi-page UX.
  // DCE strips the lazy import in real-prod builds.
  if (
    (import.meta.env.DEV || import.meta.env.VITE_ENABLE_CHANNEL_2 === "1") &&
    route === "/__channel-2"
  ) {
    const ChannelTwoPlayground = React.lazy(
      () => import("@/routes/labs/channel-2/page"),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading Channel 2 playground…
          </div>
        }
      >
        <ChannelTwoPlayground />
      </React.Suspense>
    );
  }

  // Phase A P1.6 — Channel 2 REAL backend dogfood playground.
  // /__channel-2-real — calls cogo-cloudflare /api/v3/block/generate.
  // DCE strips when not enabled.
  if (
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_CHANNEL_2_REAL === "1") &&
    route === "/__channel-2-real"
  ) {
    const ChannelTwoRealPlayground = React.lazy(
      () => import("@/routes/labs/channel-2-real/page"),
    );
    return (
      <React.Suspense
        fallback={
          <div style={{ padding: 24, fontFamily: "ui-monospace, monospace" }}>
            Loading Channel 2 real playground…
          </div>
        }
      >
        <ChannelTwoRealPlayground />
      </React.Suspense>
    );
  }

  // URL-based comparison route (no auth required)
  if (route === "/compare") return <TailarkLandingPage />;

  // Block grid test page — replaced by @cogo/ds-browser embedded entry.
  // Original 521-LOC implementation preserved at:
  //   cogo-web/_legacy/block-grid-test-page-pre-ds-browser/BlockGridTestPage.tsx
  // The route URL stays "/block-grid-test" so design-system-preview.tsx:207
  // <iframe src="/block-grid-test"> continues to work unchanged.
  if (route === "/block-grid-test") {
    const DsBrowserEmbed = React.lazy(() =>
      import("@cogo/ds-browser").then((m) => ({ default: m.DsBrowserEmbed })),
    );
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        <DsBrowserEmbed initialRoute="/blocks/card" />
      </React.Suspense>
    );
  }

  // Legacy 31-card BlockGridTestPage fixture — Phase 034 D-01..D-06.
  // /block-grid-test stays on DsBrowserEmbed (D-05); this route serves the
  // legacy 31-card layout for visual-diff baselines + design-system parity
  // specs + the DS sub-tab preview iframe (design-system-preview.tsx:207).
  // Source moved from cogo-web/_legacy/ into src/dev/block-grid-test-legacy/.
  if (route === "/block-grid-test-legacy") {
    const BlockGridTestPage = React.lazy(() =>
      import("@/dev/block-grid-test-legacy/BlockGridTestPage").then((m) => ({
        default: m.BlockGridTestPage,
      })),
    );
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        <BlockGridTestPage />
      </React.Suspense>
    );
  }

  // Public pages — no auth required
  const INFO_PAGES: Record<string, React.ReactNode> = {
    "/about": <MktAbout onNavigate={handleNavigate} />,
    "/contact": <ContactPage onNavigate={handleNavigate} />,
    // Payment-enabled pricing keeps the real product catalog and N-Genius order
    // creation while sharing the bright public cogo shell.
    '/pricing': <PricingPage onNavigate={handleNavigate} />,
    '/enterprise': <EnterprisePage onNavigate={handleNavigate} />,
    '/terms': <TermsPage onNavigate={handleNavigate} />,
    '/privacy': <PrivacyPage onNavigate={handleNavigate} />,
    '/refund': <RefundPage onNavigate={handleNavigate} />,
    '/checkout/success': <CheckoutSuccessPage onNavigate={handleNavigate} />,
    '/checkout/failure': <CheckoutFailurePage onNavigate={handleNavigate} />,
    '/checkout/pending': <CheckoutPendingPage onNavigate={handleNavigate} />,
    // `/templates` remains a compatibility alias. Renewal makes the
    // outcome-led Showroom the canonical catalogue entry point.
    '/templates': <MktShowroom onNavigate={handleNavigate} />,
    '/showroom': <MktShowroom onNavigate={handleNavigate} />,
    '/showroom/newsletter-membership': <MktShowroomProduct onNavigate={handleNavigate} />,
    '/how-it-works': <MktHowItWorks onNavigate={handleNavigate} />,
    '/proof': <MktProof onNavigate={handleNavigate} />,
    '/ownership': <MktOwnership onNavigate={handleNavigate} />,
    '/blog': <MktBlog onNavigate={handleNavigate} />,
    '/design-system': <MktDesignSystem onNavigate={handleNavigate} />,
    '/developers': <MktDevelopers onNavigate={handleNavigate} />,
  }

  // Template detail takes priority — can be triggered from any route (e.g., homepage card click)
  if (view === "template-detail") {
    return <TemplateDetailPage />;
  }

  // Public immutable preview snapshot — /p/{previewId}
  const previewSnapshotMatch = route.match(/^\/p\/([^/]+)(?:\/(review))?\/?$/);
  if (previewSnapshotMatch) {
    return (
      <React.Suspense fallback={<div className="h-screen bg-background" />}>
        <PreviewSnapshotPage
          previewId={decodeURIComponent(previewSnapshotMatch[1])}
          mode={previewSnapshotMatch[2] === "review" ? "review" : "view"}
        />
      </React.Suspense>
    );
  }

  // Standalone fullscreen app view — /app/{slug}
  const appViewMatch = route.match(/^\/app\/([^/]+)\/?$/);
  if (appViewMatch) {
    return (
      <React.Suspense fallback={<div className="h-screen bg-[#0A0A0A]" />}>
        <AppViewPage slug={appViewMatch[1]} />
      </React.Suspense>
    );
  }

  // Dynamic public routes
  if (route === '/templates/newsletter-membership') {
    return (
      <React.Suspense fallback={<div className="h-screen bg-background" />}>
        <MktShowroomProduct onNavigate={handleNavigate} />
      </React.Suspense>
    )
  }
  const templateDetailMatch = route.match(/^\/templates\/(.+)$/)
  if (templateDetailMatch) {
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        <TemplateDetailPublicPage
          onNavigate={handleNavigate}
          templateId={templateDetailMatch[1]}
        />
      </React.Suspense>
    );
  }

  // Phase 396-04 (QI-04) — Vite MDX manual site (D-04). Must be checked BEFORE
  // the generic /docs branch below so /docs/manual/en/* routes to ManualLayoutPage.
  if (route === "/docs/manual/en" || route.startsWith("/docs/manual/en/")) {
    const segments = route
      .replace(/^\/docs\/manual\/en\/?/, "")
      .split("/")
      .filter(Boolean);
    return (
      <React.Suspense fallback={<div className="p-8">Loading manual…</div>}>
        <ManualLayoutPage pathSegments={segments} />
      </React.Suspense>
    );
  }

  // /docs           → MktDocsLanding (3-col prototype layout)
  // /docs/<...>     → DocsRouter (deep 10-section site, shipped in PR #378)
  // /docs/manual/en → ManualLayoutPage (legacy MDX, checked above)
  if (route === "/docs") {
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        <MktDocsLanding onNavigate={handleNavigate} />
      </React.Suspense>
    );
  }
  if (route.startsWith("/docs/")) {
    const segments = route
      .replace(/^\/docs\/?/, "")
      .split("/")
      .filter(Boolean);
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        <DocsRouter onNavigate={handleNavigate} segments={segments} />
      </React.Suspense>
    );
  }

  const isHomepage = route === "/" || route === "/homepage";
  const infoPage = INFO_PAGES[route];

  // Appliance entry: the redirect effect above rewrites this route to
  // /studio; render a blank frame instead of flashing the cloud marketing
  // landing — or, for `/pricing` and the N-Genius `/checkout/*` outcome
  // screens, the commerce page itself — for one paint.
  if (shouldRewriteToStudioEntry(route)) {
    return <div className="h-screen bg-background" />;
  }

  if (isHomepage || infoPage) {
    return (
      <React.Suspense
        fallback={
          <div className="h-screen flex items-center justify-center bg-background" />
        }
      >
        {isHomepage ? <MktLanding onNavigate={handleNavigate} /> : infoPage}
      </React.Suspense>
    );
  }

  if (route === "/login") {
    if (isAuthLoading) {
      return (
        <div className="h-screen flex items-center justify-center bg-background">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      );
    }
    if (!session) return <AuthPage />;
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Public unknown URLs must not fall through to the authenticated Studio shell.
  if (!route.startsWith("/studio") && !route.startsWith("/ide/")) {
    return (
      <React.Suspense fallback={<div className="h-screen bg-background" />}>
        <NotFoundPage onNavigate={handleNavigate} />
      </React.Suspense>
    );
  }

  // Unknown `/studio/*` URLs get the same treatment as unknown public URLs
  // above: say so. They used to resolve to the home chat screen with the bad
  // address left in the bar, so a typo or a stale link was indistinguishable
  // from arriving at Home on purpose. Aliases and non-canonical spellings
  // do not land here — they resolve to a route, and
  // `applyCanonicalStudioPath` corrects their address — so reaching this
  // branch means the URL genuinely names nothing.
  //
  // Placed before the auth gate: being told an address is wrong should not
  // require signing in first.
  if (route.startsWith("/studio") && !isKnownStudioPathname(route)) {
    return (
      <React.Suspense fallback={<div className="h-screen bg-background" />}>
        <StudioNotFoundPage
          pathname={route}
          onNavigate={handleNavigate}
        />
      </React.Suspense>
    );
  }

  // Studio dashboard — "/studio" or any studio sub-route
  // Auth loading state
  if (isAuthLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not authenticated — skip auth in dev mode for DX, but enforce when E2E_MOCK is active
  const forceAuth = import.meta.env.VITE_E2E_MOCK === "true";
  if (!session && (!import.meta.env.DEV || forceAuth || explicitSignOut))
    return <AuthPage />;

  // Phase 5 (IA v3) — shell selection simplified to two shells. IDE
  // (workspace) uses `StudioShell`; all dashboard routes, including the
  // former commerce shell routes, render via `StudioDashboardShell`. The
  // previous `StudioCommerceShell` branch was removed — see
  // `docs/studio/menu-ia-2026-04-21.md` Phase 5 addendum.
  const onIdeRoute = route.startsWith("/ide/");
  const ideProjectId = readIdeProjectId(route);
  const ideRouteNeedsServerOpen = Boolean(
    ideProjectId && !ideProjectId.startsWith("import-"),
  );
  if (
    onIdeRoute &&
    ideRouteNeedsServerOpen &&
    view === "dashboard" &&
    !currentProject &&
    !projectLoadError
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div
          data-testid="ide-project-loading"
          className="flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm"
        >
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Opening project
        </div>
      </div>
    );
  }
  const shell: React.ReactNode =
    view !== "dashboard" || onIdeRoute ? (
      <StudioShell />
    ) : (
      <StudioDashboardShell />
    );

  return (
    <WebMCPProvider
      registry={webmcpRegistry}
      ontology={STUDIO_ONTOLOGY}
      persona={STUDIO_AGENT_CONFIG.persona}
    >
      {/* Phase 4 IA-3 — keying AssistantRuntimeProvider by currentThreadId
          remounts the assistant-ui runtime when the user crosses a thread
          boundary (Dashboard/Commerce ↔ IDE-project). Each remount starts
          a fresh in-memory transcript; useLocalRuntime doesn't persist
          across sessions, so no extra cleanup is needed. */}
      <AssistantRuntimeProvider key={currentThreadId} runtime={runtime}>
        {shell}
        {/* Brainstorm overlay — pointer-events-none fixed z-50, activates only when drawerOpen=true */}
        <BrainstormOverlayHost />
      </AssistantRuntimeProvider>
    </WebMCPProvider>
  );
}
